'use client';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useEffect } from 'react';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useSearchParams } from 'next/navigation';
import { useProfileNavigation } from '../../profile/profile-navigation';
import { createOffer } from '@eliadmin/core/src/api/jobportal/create-curriculum-api';
import React from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { deepGet } from '@eliadmin/core/src/utils/misc-utils';
import { AddOfferControls } from '../add-job-offer/add-offer-controls';
import { AddOfferStepper } from '../add-job-offer/add-offer-stepper';
import { AddOfferStep0 } from '../add-job-offer/steps/add-offer-step-0';
import { AddOfferStep1 } from '../add-job-offer/steps/add-offer-step-1';
import { AddOfferStep2 } from '../add-job-offer/steps/add-offer-step-2';
import { AddOfferStep3 } from '../add-job-offer/steps/add-offer-step-3';
import { AddOfferStep4 } from '../add-job-offer/steps/add-offer-step-4';
import { AddOfferStepsBuckets } from '../add-job-offer/steps/add-offer-steps-validator';
import type { JobPortalOfferDetail } from '@eliadmin/core/src/api/jobportal/offers-api';
import { editOffer } from '@eliadmin/core/src/api/jobportal-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { uploadSecretFile } from '@eliadmin/core/src/api/video/video-api';
import { getEditJobOfferValues, transformEditJobOfferData } from './edit-job-offer-values';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { FormErrorSummary } from '../../auth/form-error-summary';
import {
  getOfferErrorField,
  getOfferErrorStep,
  validateJobOfferBasics,
} from '../job-offer-form-validation';

export enum LanguageLevels {
  A1 = 'A1',
  A2 = 'A2',
  B1 = 'B1',
  B2 = 'B2',
  C1 = 'C1',
  C2 = 'C2',
}

export type EditJobOfferFormValues = z.infer<ReturnType<typeof editJobSchema>>;

export const EditOfferStepsBuckets = {
  ...AddOfferStepsBuckets,
  0: [...AddOfferStepsBuckets[0], 'company_name', 'address', 'postcode', 'country'],
};

export const editJobSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      categories: z.any().optional(),
      city: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      employment_types: z.string(),
      homeoffice: z.boolean().optional(),
      min_salary: z.string(),
      max_salary: z.string(),
      date_start: z.any().superRefine((value, context) => {
        if (value === null || value === undefined || value === '') {
          context.addIssue({ code: 'custom', message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE() });
        } else if (!(value instanceof Date) || !Number.isFinite(value.getTime())) {
          context.addIssue({ code: 'custom', message: VM.INVALID_DATE_VALIDATION_MESSAGE() });
        }
      }),
      contact_email: z
        .string()
        .email(VM.EMAIL_INVALID_VALIDATION_MESSAGE())
        .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      description: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      contact_phone: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      benefit: z.string().optional(),
      qualification: z.string().optional(),
      for_graduates: z.boolean().optional(),
      for_foreigners: z.boolean().optional(),
      dual_education: z.boolean().optional(),
      free_places: z.string().optional(),
      company_name: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      skills: z.any().optional().nullable(),
      languages: z.any().optional().nullable(),
      driving_licences: z.any().optional().nullable(),
      hasDrivingLicence: z.boolean().optional(),

      address: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      street_number: z.string().optional(),
      postcode: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

      salary_type: z.any(),
      company_contact_url: z.string().optional().nullable(),

      company: z
        .object({
          id: z.number(),
        })
        .optional(),

      image: z.any().optional(),

      workplace: z.any().optional(),

      suitability: z.any().optional(),

      age_range: z.any().optional(),

      working_hour: z.any().optional(),

      graduate: z.any().optional(),

      discipline: z.any().optional(),

      prerequisite: z.any().optional(),

      web_url: z.string().optional(),

      contact_name: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      contact_surname: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),

      attachments: z
        .array(
          z.object({
            value: z.number().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          }),
        )
        .optional(),

      images: z
        .array(
          z.object({
            value: z.number().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
          }),
        )
        .optional(),

      tags: z.array(z.any()).optional(),

      text_fields: z
        .array(
          z.object({
            id: z.number().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
            value: z.string().nullable(),
          }),
        )
        .optional(),

      country: z.string().trim().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      video: z.any().optional().nullable(),
      video_current: z.any().optional(),

      // Prefer top-level start_immediately in the form for editing
      start_immediately: z.boolean().optional(),
      // countries: z.array(
      //   z.object({
      //     id: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      //   }),
      // ),

      // gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      // tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      // 'g-recaptcha-response': z.string().default('').optional(),
    })
    .superRefine((data, context) => validateJobOfferBasics(data, context, VM));

type EditJobOfferFormProps = {
  workplaces: { value: number; label: string }[];
  suitabilities: { value: number; label: string }[];
  ageRanges: { value: number; label: string }[];
  graduates: { value: number; label: string }[];
  workingHours: { value: number; label: string }[];
  disciplines: { value: number; label: string }[];
  prerequisites: { value: number; label: string }[];
  languages: { value: number; label: string }[];
  drivingLicences: { value: number; label: string }[];
  countries: { value: string; label: string }[];
  employmentTypes: { value: number; label: string }[];
  jobCategories: { value: number; label: string }[];
  salaryTypes: { value: number; label: string }[];
  tags: { value: number; label: string }[];
  offer: JobPortalOfferDetail;
};

export function EditJobOfferForm({ offer, ...props }: EditJobOfferFormProps) {
  const { t } = useTranslations();
  const searchParams = useSearchParams();
  const offerSlug = searchParams.get('offerSlug');
  const { formValidationMessages } = useFormValidationMessages(t);
  const { toast } = useToast();

  const me = useCustomerStore(state => state.me);

  const navigateToProfile = useProfileNavigation();

  const form = useForm<z.infer<ReturnType<typeof editJobSchema>>>({
    mode: 'onChange',
    resolver: zodResolver(editJobSchema(formValidationMessages)),
    defaultValues: getEditJobOfferValues(offer, me?.customer),
  });

  const {
    fields: languageFields,
    append: appendLanguage,
    remove: removeLanguage,
  } = useFieldArray({
    control: form.control,
    name: 'languages',
  });

  const languages = form.watch('languages') || [];

  useEffect(() => {
    const currentLanguages = form.getValues('languages');

    // Append only if currentLanguages and languageFields are both empty
    if ((!currentLanguages || currentLanguages.length === 0) && languageFields.length === 0) {
      appendLanguage({
        language: null,
        level: null,
      });
    }
  }, []);

  const handleSubmit = form.handleSubmit(
    async data => {
      if (!me || !me.authToken) {
        form.setError('root.server', {
          message: t('Prihlásenie vypršalo. Prihláste sa znova pred uložením ponuky.'),
        });
        return;
      }
      form.clearErrors('root');
      try {
        if (data.video) {
          const formData = new FormData();

          formData.append('file', data.video);

          try {
            const res = await uploadSecretFile(formData, me?.authToken || '');
            data.company_contact_url = res.data.id.toString() as any;
          } catch (e) {
            form.setError('video', {
              message: t('Video sa nepodarilo nahrať. Skúste to znova alebo ho odstráňte.'),
            });
            throw e;
          }
        }

        await editOffer(transformEditJobOfferData(data, offer), me?.authToken, offerSlug || '');
        navigateToProfile(ROUTES.PROFILE() + '?initStep=company-job-offers');
      } catch (err: any) {
        Object.keys(err.fields || {}).forEach((key: any) => {
          if (key === 'city') {
            form.setError(key, {
              message: 'Miesto výkonu práce musí byť mesto vo vybranej krajine',
            });
            setCurrentErrorStep(0);
            return;
          }
          form.setError(getOfferErrorField(key) as any, {
            message: err.fields[key][0],
          });
        });
        form.setError('root.server', {
          message: t('Ponuku sa nepodarilo uložiť. Skontrolujte údaje a skúste to znova.'),
        });
        const errorStep = getOfferErrorStep(err.fields, EditOfferStepsBuckets);
        setCurrentStep(errorStep);
        setCurrentErrorStep(errorStep);
        toast({
          title: t('text2102'),
          variant: 'destructive',
          description: t('text2103'),
          duration: 5000,
        });
      }
    },
    errors => {
      const errorStep = getOfferErrorStep(errors, EditOfferStepsBuckets);
      setCurrentStep(errorStep);
      setCurrentErrorStep(errorStep);
    },
  );

  const [currentStep, setCurrentStep] = React.useState(0);
  const [currentErrorStep, setCurrentErrorStep] = React.useState<
    keyof typeof AddOfferStepsBuckets | null
  >(null);

  const checkIfStepInputsValid = async (step: keyof typeof AddOfferStepsBuckets) => {
    const currentBucket = EditOfferStepsBuckets[step];

    if (!currentBucket) return true; // No fields to validate

    // Trigger validation only for fields in the current bucket
    const isValid = await form.trigger(currentBucket as any, { shouldFocus: true });

    // Check for errors (handle nested schemas with deepGet if needed)
    const hasErrors = currentBucket.some(field => {
      const error = deepGet(form.formState.errors, field);
      return !!error;
    });

    if (!isValid || hasErrors) {
      setCurrentErrorStep(step);
      return false;
    }
    setCurrentErrorStep(null);
    return true; // Step is valid
  };

  const handleSetCurrentStep = async (step: number) => {
    const isValid = await checkIfStepInputsValid(currentStep as keyof typeof AddOfferStepsBuckets);
    if (isValid) {
      setCurrentStep(step);
    }
  };

  return (
    <FormProvider {...form}>
      <form
        className={cn('flex w-full items-start justify-start gap-2 py-4', 'ea-add-job-offer-form')}
        onSubmit={handleSubmit}
      >
        <AddOfferStepper
          currentStep={currentStep}
          setCurrentStep={handleSetCurrentStep}
          currentErrorStep={currentErrorStep}
          defaultAllCompleted
        />
        <div className="flex w-full flex-col gap-4">
          <FormErrorSummary errors={form.formState.errors} />
          {currentStep === 0 && (
            <AddOfferStep0
              categories={props.jobCategories}
              employmentTypes={props.employmentTypes}
              isEdit
            />
          )}
          {currentStep === 1 && <AddOfferStep1 />}
          {currentStep === 2 && <AddOfferStep2 />}
          {currentStep === 3 && <AddOfferStep3 />}
          {currentStep === 4 && <AddOfferStep4 />}
          <AddOfferControls
            currentStep={currentStep}
            setCurrentStep={handleSetCurrentStep}
            handleSubmit={handleSubmit}
            editMode
            disabled={form.formState.isSubmitting}
          />
        </div>
      </form>
    </FormProvider>
  );
}
